import type { Call } from '@/shared/services/api/types';

import type { CallEndReason, CallPhase } from '@/stores/call.store';

/** Qui l'écran montre en face : une personne, une boutique, ou un groupe. */
export interface CallCounterpart {
  name: string;
  photoUrl: string | null;
  kind: 'person' | 'shop';
  /** Le groupe d'où vient l'appel, sous le nom de qui appelle. */
  groupTitle: string | null;
}

/**
 * Qui est en face, vu de cet appareil.
 *
 * - Appelé : la personne qui appelle, avec le groupe d'où vient l'appel.
 * - Appelant d'un groupe : le groupe.
 * - Appelant d'une boutique que toute une équipe tient : la boutique, pas la
 *   liste de ses membres ; sinon, la personne appelée.
 */
export function counterpartOf(call: Call, myId: string | null): CallCounterpart {
  const group = call.conversationType === 'GROUP';
  if (call.callerId !== myId) {
    const caller = call.participants.find((person) => person.id === call.callerId);
    return {
      name: caller?.displayName ?? '',
      photoUrl: caller?.photoUrl ?? null,
      kind: 'person',
      groupTitle: group ? (call.groupTitle ?? null) : null,
    };
  }
  if (group) {
    return { name: call.groupTitle ?? '', photoUrl: null, kind: 'person', groupTitle: null };
  }
  const others = call.participants.filter((person) => person.id !== myId);
  const answered = others.find((person) => person.state === 'JOINED');
  if (call.shop && others.length > 1 && !answered) {
    return {
      name: call.shop.name,
      photoUrl: call.shop.logoUrl ?? null,
      kind: 'shop',
      groupTitle: null,
    };
  }
  const shown = answered ?? others[0];
  return {
    name: shown?.displayName ?? '',
    photoUrl: shown?.photoUrl ?? null,
    kind: 'person',
    groupTitle: null,
  };
}

/** Ce que dit la ligne d'état sous le nom : un texte, ou le chrono. */
export type CallStatusLine =
  | { kind: 'timer' }
  | {
      kind: 'text';
      key:
        | 'status.calling'
        | 'status.ringing'
        | 'status.connecting'
        | 'status.reconnecting'
        | 'status.alone'
        | 'incoming.AUDIO'
        | 'incoming.VIDEO'
        | 'incoming.rejoin'
        | `ended.${CallEndReason}`;
    };

export function statusLineOf(options: {
  phase: CallPhase;
  call: Call | null;
  myId: string | null;
  reconnecting: boolean;
  endReason: CallEndReason | null;
  remoteCount: number;
}): CallStatusLine {
  const { phase, call, myId, reconnecting, endReason, remoteCount } = options;
  switch (phase) {
    case 'incoming': {
      const mine = call?.participants.find((person) => person.id === myId)?.state;
      if (mine === 'JOINED') {
        return { kind: 'text', key: 'incoming.rejoin' };
      }
      return { kind: 'text', key: call?.kind === 'VIDEO' ? 'incoming.VIDEO' : 'incoming.AUDIO' };
    }
    case 'outgoing':
      return { kind: 'text', key: call ? 'status.ringing' : 'status.calling' };
    case 'connecting':
      return { kind: 'text', key: reconnecting ? 'status.reconnecting' : 'status.connecting' };
    case 'active':
      if (reconnecting) {
        return { kind: 'text', key: 'status.reconnecting' };
      }
      if (remoteCount === 0) {
        return {
          kind: 'text',
          key: call?.conversationType === 'GROUP' ? 'status.alone' : 'status.reconnecting',
        };
      }
      return { kind: 'timer' };
    case 'ended':
      return { kind: 'text', key: `ended.${endReason ?? 'ANSWERED'}` };
  }
}

/** « 02:13 », « 1:02:13 » : le chrono d'un appel, chiffres tabulaires. */
export function elapsedLabel(milliseconds: number): string {
  const total = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}
